import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Resolve Approval Recipients Step
 *
 * Given a customer plus an approval level number for either the discount or
 * order system, returns the email addresses of the assigned approvers. This
 * is the approval-side counterpart to `resolve_entity_watchers`: it reads
 * from the `ar_*_approval_assignments` tables instead of `core_watches`,
 * applies the same notification-preference filter, and honors the optional
 * per-assignment email override.
 *
 * Wire shape: `discountLevel` and `orderLevel` are mutually exclusive — one
 * is non-null per run, the other is null. The system in play is implicit in
 * which input socket has data, not carried as a separate discriminator.
 *
 * Returns an empty list when:
 *   - Both `discountLevel` and `orderLevel` are null (nothing to resolve).
 *   - No assignments match for the given customer + level.
 *   - The matching users have all been excluded or have the notification
 *     preference set to false.
 *
 * Throws when both `discountLevel` and `orderLevel` are non-null — the
 * caller must pick one system per emission. Throwing rather than picking
 * a winner forces upstream bugs to surface loudly.
 */
export const resolveApprovalRecipientsStep = defineStepConfig({
  type: "resolve_approval_recipients",
  name: "Resolve Approval Recipients",
  description:
    "Resolves email addresses for the assigned approvers at a given level for a given customer. Reads from approval assignment tables, honors notification preferences and per-assignment email overrides.",
  category: "Data Access",
  deprecated: {
    reason:
      "Legacy discount/order-approval flow. Superseded by the workflow-native Approval step, which resolves the assigned team's members (+ out-of-office delegates) itself instead of reading the retired approval-assignment tables.",
    replacedBy: "approval",
  },
  inputSchema: z.object({
    customerId: z
      .uuid()
      .nullable()
      .describe(
        "The customer whose approvers should be notified. Null returns " +
          "an empty result — assignments are always customer-scoped or " +
          "org-wide, but org-wide rows still need a customer to bind the " +
          "lookup against.",
      ),
    discountLevel: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(
        "Discount approval level number to notify. Set when the upstream " +
          "step decided the next action is a discount approval. Null " +
          "otherwise. Mutually exclusive with `orderLevel`.",
      ),
    orderLevel: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(
        "Order approval level number to notify. Set when the upstream " +
          "step decided the next action is an order-level approval. Null " +
          "otherwise. Mutually exclusive with `discountLevel`.",
      ),
    excludeUserIds: z
      .array(z.uuid())
      .nullable()
      .optional()
      .describe(
        "User IDs to exclude from the recipient list. Use this to suppress " +
          "notifications for the user who just triggered the workflow.",
      ),
  }),

  outputSchema: z.object({
    recipientEmails: z
      .array(z.string())
      .describe("Email addresses of resolved approvers."),
    recipientCount: z
      .number()
      .describe(
        "Number of distinct approvers resolved (before preference filtering).",
      ),
  }),

  configSchema: z.object({
    notificationPreferenceKey: z
      .string()
      .optional()
      .describe(
        "Optional user notification preference key. When set, users with " +
          "this preference set to false are excluded from the recipient list.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
    },
  },
});
